import type * as THREE from "three";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";

type Props = {
	content: string;
	align?: "left" | "right" | "center";
	style?: Partial<Pick<CSSStyleDeclaration, "color" | "backgroundColor" | "padding" | "borderRadius">>;
};

const DEFAULT_STYLE: Partial<CSSStyleDeclaration> = {
	color: "#ffffff",
	backgroundColor: "rgba(17, 24, 39, 0.9)",
	padding: "4px 6px",
	borderRadius: "2px",
	fontFamily: "monospace",
	fontWeight: "600",
	fontSize: "10px",
	lineHeight: "1",
	whiteSpace: "nowrap",
	pointerEvents: "none",
	position: "absolute",
	transition: "opacity 0.2s linear",
	opacity: "1",
};

class Label {
	public readonly sceneObject: CSS2DObject;
	private readonly labelElement: HTMLDivElement;

	constructor({ content, align = "center", style = {} }: Props) {
		const anchorElement = document.createElement("div");
		anchorElement.style.position = "relative";

		this.labelElement = document.createElement("div");
		this.setStyle({ ...DEFAULT_STYLE, ...style });

		switch (align) {
			case "left":
				this.setStyle({
					right: "24px",
					top: "50%",
					transform: "translate(0, -50%)",
				});

				break;

			case "right":
				this.setStyle({
					left: "24px",
					top: "50%",
					transform: "translate(0, -50%)",
				});

				break;

			case "center":
				this.setStyle({
					left: "0",
					top: "50%",
					transform: "translate(-50%, -50%)",
				});
				break;
		}

		this.labelElement.innerHTML = content;
		anchorElement.append(this.labelElement);

		this.sceneObject = new CSS2DObject(anchorElement);
	}

	public dispose() {
		this.sceneObject.element.remove();
		this.sceneObject.removeFromParent();
	}

	public update({ position, content, isVisible, style }: { position?: THREE.Vector3; content?: string; isVisible?: boolean; style?: Props["style"] }) {
		if (position !== undefined) {
			this.sceneObject.position.copy(position);
		}

		if (content !== undefined) {
			this.labelElement.innerHTML = content;
		}

		if (isVisible !== undefined) {
			this.labelElement.style.opacity = isVisible ? "1" : "0";
		}

		if (style !== undefined) {
			this.setStyle(style);
		}
	}

	private setStyle(style: Partial<CSSStyleDeclaration>) {
		Object.assign(this.labelElement.style, style);
	}
}

export { Label };
